// Holiday Management Types for Philippine Payroll System

export interface PhilippineHoliday {
  id: string;
  name: string;
  date: string;
  year: number;
  type: 'regular' | 'special_non_working' | 'special_working';
  description?: string;
  is_nationwide: boolean;
  region?: string;
  created_at: string;
  updated_at: string;
}

export interface HolidayWorkRecord {
  id: string;
  employee_id: string;
  holiday_id: string;
  date: string;
  hours_worked: number;
  regular_pay: number;
  holiday_pay: number;
  additional_pay: number;
  total_pay: number;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Joined data
  holiday?: PhilippineHoliday;
  employee?: {
    first_name: string;
    last_name: string;
    position: string;
    department: string;
  };
}

export interface HolidayPayCalculation {
  regular_pay: number;
  holiday_pay: number;
  additional_pay: number;
  total_pay: number;
}

export interface HolidayCheckResult {
  is_holiday: boolean;
  holiday_name?: string;
  holiday_type?: 'regular' | 'special_non_working' | 'special_working';
  holiday_id?: string;
}

export interface HolidayPaySummary {
  employee_id: string;
  employee_name: string;
  total_holiday_hours: number;
  total_holiday_pay: number;
  total_additional_pay: number;
  total_earnings: number;
  holidays_worked: {
    holiday_name: string;
    date: string;
    type: string;
    hours_worked: number;
    pay_earned: number;
  }[];
}

// Holiday pay rates according to Philippine Labor Code
export const HOLIDAY_PAY_RATES = {
  REGULAR_HOLIDAY: {
    base_rate: 2.0, // 200% (double pay)
    additional_rate: 0,
    total_rate: 2.0 // 200% total
  },
  SPECIAL_NON_WORKING: {
    base_rate: 1.3, // 130% if worked
    additional_rate: 0,
    total_rate: 1.3
  },
  SPECIAL_WORKING: {
    base_rate: 1.0, // 100% (normal pay)
    additional_rate: 0,
    total_rate: 1.0
  }
} as const;

export type HolidayType = keyof typeof HOLIDAY_PAY_RATES;

// Helper function to get holiday pay rate
export function getHolidayPayRate(holidayType: PhilippineHoliday['type']) {
  switch (holidayType) {
    case 'regular':
      return HOLIDAY_PAY_RATES.REGULAR_HOLIDAY;
    case 'special_non_working':
      return HOLIDAY_PAY_RATES.SPECIAL_NON_WORKING;
    case 'special_working':
      return HOLIDAY_PAY_RATES.SPECIAL_WORKING;
    default:
      return HOLIDAY_PAY_RATES.SPECIAL_WORKING;
  }
}

// Helper function to format holiday type for display
export function formatHolidayType(type: PhilippineHoliday['type']): string {
  switch (type) {
    case 'regular':
      return 'Regular Holiday';
    case 'special_non_working':
      return 'Special Non-Working Holiday';
    case 'special_working':
      return 'Special Working Holiday';
    default:
      return 'Unknown Holiday Type';
  }
}

// Helper function to get holiday pay description
export function getHolidayPayDescription(type: PhilippineHoliday['type']): string {
  switch (type) {
    case 'regular':
      return '200% of regular pay';
    case 'special_non_working':
      return '130% of regular pay (if worked)';
    case 'special_working':
      return 'Normal pay (100%)';
    default:
      return 'Standard pay rate';
  }
}